import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useT } from "../lib/i18n/store.js";

/** Options the owner can tick when deleting someone else's comment. */
export interface DeleteCommentOptions {
  deleteAll: boolean;
  block: boolean;
}

/**
 * Confirmation popup for deleting a comment. For the novel owner deleting another
 * user's comment it also offers two moderation options — delete ALL of that
 * user's comments, and block the user — as checkboxes. For a plain self-delete
 * (or when the viewer isn't the owner) it's just a confirm. Portaled to
 * document.body like the other modals.
 */
export function DeleteCommentModal({
  canModerate,
  onConfirm,
  onClose
}: {
  /** True when the viewer is the novel owner and this isn't their own comment. */
  canModerate: boolean;
  onConfirm: (options: DeleteCommentOptions) => Promise<void>;
  onClose: () => void;
}) {
  const t = useT();
  const [deleteAll, setDeleteAll] = useState(false);
  const [block, setBlock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pointerDownOnBackdrop = useRef(false);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, []);

  async function confirm() {
    setBusy(true);
    setError(false);
    try {
      await onConfirm({ deleteAll, block });
      onClose();
    } catch {
      setError(true);
      setBusy(false);
    }
  }

  return createPortal(
    <div
      className="admin-modal-backdrop"
      onMouseDown={(event) => {
        pointerDownOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pointerDownOnBackdrop.current) onClose();
      }}
    >
      <div
        tabIndex={-1}
        className="admin-modal comment-delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="comment-delete-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="admin-modal-close" aria-label={t("popup.close")} onClick={onClose}>
          ×
        </button>
        <h2 id="comment-delete-title">{t("comments.deleteTitle")}</h2>

        {canModerate && (
          <div className="comment-delete-options">
            <label className="comment-delete-option">
              <input type="checkbox" checked={deleteAll} onChange={(e) => setDeleteAll(e.target.checked)} />
              <span>{t("comments.optDeleteAll")}</span>
            </label>
            <label className="comment-delete-option">
              <input type="checkbox" checked={block} onChange={(e) => setBlock(e.target.checked)} />
              <span>{t("comments.optBlock")}</span>
            </label>
          </div>
        )}

        {error && <p className="invalid">{t("comments.error")}</p>}

        <div className="comment-delete-actions">
          <button type="button" className="secondary" onClick={onClose}>
            {t("comments.cancel")}
          </button>
          <button
            type="button"
            ref={confirmRef}
            className="comment-delete-confirm"
            disabled={busy}
            onClick={() => void confirm()}
          >
            {t("comments.delete")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
